import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { geoInterpolate, geoPath, geoProjection } from "d3-geo";

import { arcBetween, cameraFor, isFacing, loadWorld, satelliteRaw } from "../../tradeFeed/globeGeometry.js";
import { regionLabel, regionPoint, routeKey } from "./arbitrageRegions.js";

/**
 * One reference's geographic arbitrage on a globe you can turn (Alex,
 * 2026-10-12).
 *
 * Each REGION is a node at its market's centroid, labelled with its median
 * trade and market price. Each LINE joins two regions where buying in one and
 * selling in the other clears the 5% floor, labelled with the gap (Price gap)
 * or the supply-pressure score (Supply pressure). Node size follows the view:
 * constant for Price gap, area-proportional to quotes for Supply pressure, so
 * "plentiful here, scarce there" is visible before any number is read.
 *
 * Everything drawn comes from the legs the card already holds — no request is
 * made when the popup opens. The base map is the shared, memoised atlas, which
 * the grid starts loading when the page mounts.
 *
 * Canvas, rotation in a ref, labels at constant screen size, and an accessible
 * list beside it: the same choices as TradeFeedGlobe, for the same reasons.
 */

const FALLBACK_SIZE = 420;
const MAX_SIZE = 560;
const DRAG_SENSITIVITY = 0.25;
const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 1.35;
const MIN_HIT_RADIUS = 22;
const TURN_MS = 450;

const nodeRadius = (quotes, maxQuotes, sized) =>
  sized ? 5 + 17 * Math.sqrt(quotes / (maxQuotes || 1)) : 7;

/** Money in MAJOR units for a canvas label, from minor units. */
const moneyLabel = (minor, currency) => {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: String(currency || "gbp").toUpperCase(),
      maximumFractionDigits: 0,
    }).format(minor / 100);
  } catch {
    return Math.round(minor / 100).toLocaleString();
  }
};

/** Rotation that puts [lon, lat] in the middle of the disc. */
const rotationFacing = ([lon, lat]) => [-lon, Math.max(-60, Math.min(60, -lat))];

/** Lines beyond the focused one that also get a label; the rest stay quiet. */
const LABELLED_LINES = 3;

const LABEL_FONT = "600 11px system-ui, -apple-system, 'Segoe UI', sans-serif";

/** Screen box a pill would occupy, for the collision test. */
const pillBox = (context, text, x, y) => {
  context.font = LABEL_FONT;
  const width = context.measureText(text).width + 12;
  return { x: x - width / 2, y: y - 9, w: width, h: 18 };
};

const overlaps = (a, b) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

const pill = (context, text, x, y, fill, colour) => {
  context.font = LABEL_FONT;
  const width = context.measureText(text).width + 12;
  context.beginPath();
  context.roundRect(x - width / 2, y - 9, width, 18, 9);
  context.fillStyle = fill;
  context.fill();
  context.fillStyle = colour;
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillText(text, x, y + 0.5);
  return width;
};

const ArbitrageGlobe = ({ legs = [], routes = [], focusKey, view = "price_gaps", currency = "gbp", onFocus }) => {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const [topology, setTopology] = useState(null);
  const [size, setSize] = useState(FALLBACK_SIZE);
  const [zoom, setZoom] = useState(1);
  const rotationRef = useRef([0, -20]);
  const zoomRef = useRef(1);
  const dragRef = useRef(null);
  const frameRef = useRef(0);
  const turnRef = useRef(0);

  const supplyView = view === "supply_pressure";
  const focus = routes.find((route) => routeKey(route) === focusKey) || null;

  /* ── Regions and lines, from the legs ─────────────────────────────────── */

  const nodes = useMemo(() => {
    const byRegion = {};
    legs.forEach((leg) => {
      const point = regionPoint(leg.region);
      if (!point) return;
      const node = byRegion[leg.region] || (byRegion[leg.region] = { region: leg.region, point, quotes: 0 });
      node[leg.pool] = leg;
      node.quotes += leg.quotes;
    });
    const list = Object.values(byRegion);
    const maxQuotes = Math.max(1, ...list.map((n) => n.quotes));
    return list.map((n) => ({ ...n, radius: nodeRadius(n.quotes, maxQuotes, supplyView) }));
  }, [legs, supplyView]);

  // One line per pair of regions — the strongest route between them for this
  // view. Same-region trade↔market routes have no line to draw; they stay in
  // the list beside the globe.
  const lines = useMemo(() => {
    const best = {};
    routes.forEach((route) => {
      if (route.buy.region === route.sell.region) return;
      const a = regionPoint(route.buy.region);
      const b = regionPoint(route.sell.region);
      if (!a || !b) return;
      const pairKey = [route.buy.region, route.sell.region].sort().join("~");
      const value = supplyView ? route.score : route.gapPercent;
      if (!best[pairKey] || value > best[pairKey].value) {
        best[pairKey] = { route, key: routeKey(route), value, line: arcBetween(a, b), midpoint: geoInterpolate(a, b)(0.5) };
      }
    });
    // The focused route always gets its own line, even if another route
    // between the same two regions ranks higher.
    if (focus && focus.buy.region !== focus.sell.region) {
      const a = regionPoint(focus.buy.region);
      const b = regionPoint(focus.sell.region);
      if (a && b) {
        const pairKey = [focus.buy.region, focus.sell.region].sort().join("~");
        best[pairKey] = {
          route: focus,
          key: routeKey(focus),
          value: supplyView ? focus.score : focus.gapPercent,
          line: arcBetween(a, b),
          midpoint: geoInterpolate(a, b)(0.5),
        };
      }
    }
    return Object.values(best);
  }, [routes, focus, supplyView]);

  /* ── Base map: shared and usually already loaded ──────────────────────── */

  useEffect(() => {
    let cancelled = false;
    loadWorld().then((countries) => { if (!cancelled) setTopology(countries); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const node = wrapRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (width > 0) setSize(Math.round(Math.min(width, MAX_SIZE)));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /* ── Painting ─────────────────────────────────────────────────────────── */

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext("2d");
    if (!context) return;

    const styles = getComputedStyle(canvas);
    const token = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;
    const ocean = token("--arb-globe-ocean", "#1a1a1a");
    const land = token("--arb-globe-land", "#242424");
    const hairline = token("--arb-globe-hairline", "rgba(255,255,255,.14)");
    const accent = token("--arb-globe-accent", "#7145d0");
    const muted = token("--arb-globe-muted", "rgba(255,255,255,.35)");
    const text = token("--arb-globe-text", "#fff");
    const buyColour = token("--arb-globe-buy", "#4ac08a");
    const sellColour = token("--arb-globe-sell", "#d9a441");
    const labelBg = token("--arb-globe-label-bg", "rgba(20,20,24,.86)");

    const ratio = window.devicePixelRatio || 1;
    canvas.width = size * ratio;
    canvas.height = size * ratio;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.clearRect(0, 0, size, size);

    const disc = size / 2 - 2;
    const { P, cap, scale } = cameraFor(zoomRef.current, disc);
    const rotation = rotationRef.current;
    const projection = geoProjection(satelliteRaw(P))
      .scale(scale)
      .translate([size / 2, size / 2])
      .clipAngle((Math.acos(1 / P) * 180) / Math.PI - 0.01)
      .rotate(rotation);
    const path = geoPath(projection, context);

    context.beginPath();
    context.arc(size / 2, size / 2, disc, 0, 2 * Math.PI);
    context.fillStyle = ocean;
    context.fill();
    context.lineWidth = 1;
    context.strokeStyle = hairline;
    context.stroke();

    if (topology) {
      context.beginPath();
      path(topology);
      context.fillStyle = land;
      context.fill();
      context.lineWidth = 0.4;
      context.strokeStyle = hairline;
      context.stroke();
    }

    // Lines: the focused route solid in the accent, the rest dotted and
    // quieter, so the one you opened reads first.
    const ordered = [...lines].sort((a, b) => (a.key === focusKey) - (b.key === focusKey));
    ordered.forEach((item) => {
      const isFocus = item.key === focusKey;
      context.save();
      context.setLineDash(isFocus ? [] : [2, 4]);
      context.lineWidth = isFocus ? 2.5 : 1.25;
      context.strokeStyle = isFocus ? accent : muted;
      context.beginPath();
      path(item.line);
      context.stroke();
      context.restore();
    });

    const hits = [];
    // Every label drawn so far. A label that would overlap one is skipped
    // rather than stacked: zooming in spreads the marks and brings it back.
    const placed = [];
    const tryPill = (texts, anchorX, y, fills) => {
      // Slide sideways to stay on the canvas — near the rim a label would
      // otherwise be dropped exactly where the route ends.
      const widest = Math.max(...texts.map((t) => pillBox(context, t, 0, 0).w));
      const x = Math.min(size - 2 - widest / 2, Math.max(2 + widest / 2, anchorX));
      const boxes = texts.map((t, i) => pillBox(context, t, x, y + i * 19));
      // Whole label on the canvas — a half-drawn price is worse than none.
      const inside = boxes.every((b) => b.x >= 2 && b.y >= 2 && b.x + b.w <= size - 2 && b.y + b.h <= size - 2);
      if (!inside || boxes.some((b) => placed.some((q) => overlaps(b, q)))) return false;
      texts.forEach((t, i) => pill(context, t, x, y + i * 19, fills[i], text));
      placed.push(...boxes);
      return true;
    };

    const isEnd = (node) => focus && (focus.buy.region === node.region || focus.sell.region === node.region);
    const nodeOrder = [...nodes].sort((a, b) => (isEnd(b) - isEnd(a)) || (b.quotes - a.quotes));

    // Regions: dots first, labels after, so a label is never under a dot.
    const nodeLabels = [];
    nodeOrder.forEach((node) => {
      if (!isFacing(node.point, rotation, cap)) return;
      const [x, y] = projection(node.point);
      const isBuy = focus && focus.buy.region === node.region;
      const isSell = focus && focus.sell.region === node.region;

      context.beginPath();
      context.arc(x, y, node.radius, 0, 2 * Math.PI);
      context.fillStyle = isBuy ? buyColour : isSell ? sellColour : accent;
      context.globalAlpha = supplyView ? 0.8 : 1;
      context.fill();
      context.globalAlpha = 1;
      context.lineWidth = 1.5;
      context.strokeStyle = text;
      context.stroke();

      // Name, then each pool's median: the number the line's gap is made of.
      const prices = [
        node.trade ? `T ${moneyLabel(node.trade.median, currency)}` : null,
        node.market ? `M ${moneyLabel(node.market.median, currency)}` : null,
      ].filter(Boolean).join(" · ");
      nodeLabels.push({ node, x, y, prices });
      hits.push({ kind: "node", region: node.region, x, y, r: node.radius });
    });

    // The focused line's number first — it must always show — then the
    // strongest few others, then the regions' names and medians.
    const lineValue = (item) => (supplyView ? item.route.score : item.route.gapPercent);
    const lineOrder = [...lines].sort((a, b) => ((b.key === focusKey) - (a.key === focusKey)) || (lineValue(b) - lineValue(a)));
    lineOrder.forEach((item, index) => {
      if (!isFacing(item.midpoint, rotation, cap)) return;
      const [x, y] = projection(item.midpoint);
      const isFocus = item.key === focusKey;
      hits.push({ kind: "line", key: item.key, x, y, r: 14 });
      if (!isFocus && index > LABELLED_LINES) return;
      const label = supplyView ? `score ${item.route.score}` : `+${item.route.gapPercent}%`;
      tryPill([label], x, y, [isFocus ? accent : labelBg]);
    });

    nodeLabels.forEach(({ node, x, y, prices }) => {
      const top = y - node.radius - 26;
      tryPill([regionLabel(node.region), prices], x, top, [labelBg, labelBg]) ||
        // No room above — try below the dot before giving up.
        tryPill([regionLabel(node.region), prices], x, y + node.radius + 14, [labelBg, labelBg]);
    });

    canvas._hits = hits;
  }, [size, topology, lines, nodes, focus, focusKey, supplyView, currency]);

  const repaint = useCallback(() => {
    cancelAnimationFrame(frameRef.current);
    frameRef.current = requestAnimationFrame(draw);
  }, [draw]);

  useEffect(() => {
    repaint();
    return () => cancelAnimationFrame(frameRef.current);
  }, [repaint]);

  /* ── Turn to the focused route, smoothly ──────────────────────────────── */

  useEffect(() => {
    if (!focus) return undefined;
    const a = regionPoint(focus.buy.region);
    const b = regionPoint(focus.sell.region);
    if (!a || !b) return undefined;

    const target = rotationFacing(geoInterpolate(a, b)(0.5));
    const from = [...rotationRef.current];
    // Take the short way round in longitude.
    const dLon = ((target[0] - from[0] + 540) % 360) - 180;
    const dLat = target[1] - from[1];
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const start = performance.now();

    cancelAnimationFrame(turnRef.current);
    const step = (now) => {
      const t = reduced ? 1 : Math.min(1, (now - start) / TURN_MS);
      const ease = 1 - (1 - t) ** 3;
      rotationRef.current = [from[0] + dLon * ease, from[1] + dLat * ease];
      draw();
      if (t < 1) turnRef.current = requestAnimationFrame(step);
    };
    turnRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(turnRef.current);
    // Only when the focused route changes — a repaint must not restart the turn.
  }, [focusKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Pointer, wheel and buttons ───────────────────────────────────────── */

  const applyZoom = useCallback((next) => {
    const clamped = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
    if (clamped === zoomRef.current) return;
    zoomRef.current = clamped;
    setZoom(clamped);
    repaint();
  }, [repaint]);

  useEffect(() => {
    const node = canvasRef.current;
    if (!node) return undefined;
    const onWheel = (event) => {
      event.preventDefault();
      applyZoom(zoomRef.current * (event.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP));
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [applyZoom]);

  const hitAt = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas?.getBoundingClientRect();
    if (!rect || !canvas._hits) return null;
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    let best = null;
    let bestDistance = Infinity;
    canvas._hits.forEach((hit) => {
      const distance = Math.hypot(hit.x - x, hit.y - y);
      if (distance <= Math.max(hit.r, MIN_HIT_RADIUS) && distance < bestDistance) {
        best = hit;
        bestDistance = distance;
      }
    });
    return best;
  };

  const onPointerDown = (event) => {
    cancelAnimationFrame(turnRef.current);
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = { x: event.clientX, y: event.clientY, rotation: [...rotationRef.current], moved: false };
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) drag.moved = true;
    const k = DRAG_SENSITIVITY / zoomRef.current;
    rotationRef.current = [drag.rotation[0] + dx * k, Math.max(-90, Math.min(90, drag.rotation[1] - dy * k))];
    repaint();
  };

  const onPointerUp = (event) => {
    const drag = dragRef.current;
    dragRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (!drag || drag.moved) return;

    const hit = hitAt(event);
    if (hit?.kind === "line") onFocus?.(hit.key);
    if (hit?.kind === "node") {
      // A region: focus its strongest route, buying or selling there.
      const value = (r) => (supplyView ? r.score : r.gapPercent);
      const candidate = routes
        .filter((r) => r.buy.region === hit.region || r.sell.region === hit.region)
        .sort((a, b) => value(b) - value(a))[0];
      if (candidate) onFocus?.(routeKey(candidate));
    }
  };

  return (
    <div className="arb-globe" ref={wrapRef}>
      <div className="arb-globe__stage" style={{ width: size, height: size }}>
        <canvas
          ref={canvasRef}
          className="arb-globe__canvas"
          style={{ width: size, height: size }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          // The route list beside the globe is the accessible control surface.
          aria-hidden="true"
        />
        <div className="arb-globe__zoom">
          <button type="button" onClick={() => applyZoom(zoomRef.current * ZOOM_STEP)} disabled={zoom >= MAX_ZOOM} aria-label="Zoom in">+</button>
          <button type="button" onClick={() => applyZoom(zoomRef.current / ZOOM_STEP)} disabled={zoom <= MIN_ZOOM} aria-label="Zoom out">−</button>
        </div>
      </div>
    </div>
  );
};

export default ArbitrageGlobe;
